import { useState } from 'react';
import { Plus, Pencil, Trash2, HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { API } from './constants';

export default function FAQsTab({ faqs, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [form, setForm] = useState({
    question: '', answer: '', category: 'general', audience: 'customer', order: 0
  });

  const resetForm = () => {
    setForm({ question: '', answer: '', category: 'general', audience: 'customer', order: 0 });
    setEditingFaq(null);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || 'general',
      audience: faq.audience || 'customer',
      order: faq.order || 0
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.question || !form.answer) {
      toast.error('Pregunta y respuesta son requeridas');
      return;
    }

    try {
      const url = editingFaq 
        ? `${API}/admin/faqs/${editingFaq.faq_id}`
        : `${API}/admin/faqs`;
      
      const response = await fetch(url, {
        method: editingFaq ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(editingFaq ? 'FAQ actualizada' : 'FAQ creada');
        setDialogOpen(false);
        resetForm();
        onRefresh?.();
      } else {
        toast.error('Error al guardar FAQ');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('¿Eliminar esta FAQ?')) return;
    
    try {
      const response = await fetch(`${API}/admin/faqs/${faqId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('FAQ eliminada');
        onRefresh?.();
      }
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const categoryLabels = {
    general: 'General',
    delivery: 'Entregas',
    orders: 'Pedidos',
    payments: 'Pagos',
    subscriptions: 'Suscripciones'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900">Preguntas Frecuentes</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-emerald-700 hover:bg-emerald-800 rounded-full"
              onClick={resetForm}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva FAQ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingFaq ? 'Editar FAQ' : 'Nueva FAQ'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Pregunta *</label>
                <Input
                  value={form.question}
                  onChange={(e) => setForm({...form, question: e.target.value})}
                  className="mt-1"
                  placeholder="¿Cómo puedo...?"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Respuesta *</label>
                <Textarea
                  value={form.answer}
                  onChange={(e) => setForm({...form, answer: e.target.value})}
                  className="mt-1 min-h-[120px]"
                  placeholder="La respuesta a la pregunta..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700">Categoría</label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="delivery">Entregas</SelectItem>
                      <SelectItem value="orders">Pedidos</SelectItem>
                      <SelectItem value="payments">Pagos</SelectItem>
                      <SelectItem value="subscriptions">Suscripciones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Audiencia</label>
                  <Select value={form.audience} onValueChange={(v) => setForm({...form, audience: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Clientes</SelectItem>
                      <SelectItem value="admin">Administradores</SelectItem>
                      <SelectItem value="all">Todos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Orden</label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({...form, order: parseInt(e.target.value)})}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSave} className="w-full bg-emerald-700 hover:bg-emerald-800">
                {editingFaq ? 'Actualizar' : 'Crear'} FAQ
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* FAQs List */}
      <div className="space-y-4">
        {faqs.map(faq => (
          <div key={faq.faq_id} className="bg-white rounded-2xl border border-stone-200 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-stone-900">{faq.question}</h3>
                </div>
                <p className="text-stone-600 whitespace-pre-wrap">{faq.answer}</p>
                <div className="flex gap-2 mt-3">
                  <Badge variant="outline">{categoryLabels[faq.category] || faq.category}</Badge>
                  <Badge variant="outline" className="bg-stone-50">
                    {faq.audience === 'customer' ? 'Clientes' : faq.audience === 'admin' ? 'Admins' : 'Todos'}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(faq)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(faq.faq_id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {faqs.length === 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <HelpCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500">No hay preguntas frecuentes</p>
        </div>
      )}
    </div>
  );
}
